import React, { useState } from 'react';
import { formatAmount, formatDate, getStatusColor } from './utils/formatters';
import { ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import type { TransactionWithFees, MerchantFee, BankFee, Bank } from './types';

interface TransactionTableProps {
  transactions: TransactionWithFees[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  filters: {
    status: string;
    method: string;
    bank: string;
  };
  onFilterChange: (filters: { status: string; method: string; bank: string }) => void;
}

const ITEMS_PER_PAGE = 50; // Increased items per page

export function TransactionTable({ 
  transactions, 
  currentPage,
  totalPages,
  onPageChange,
  filters,
  onFilterChange
}: TransactionTableProps) {
  const [showFilters, setShowFilters] = useState(false);

  const uniqueStatuses = [...new Set(transactions.map(t => t.status))];
  const uniqueMethods = [...new Set(transactions.map(t => t.method))];
  const uniqueBanks = [...new Set(transactions.map(t => t.bank))];

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  return (
    <div className="overflow-x-auto">
      {/* Filters */}
      <div className="mb-4 flex justify-between items-center">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Filter className="w-4 h-4 mr-2" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {showFilters && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              value={filters.status}
              onChange={(e) => onFilterChange({ ...filters, status: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">All Statuses</option>
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Method</label>
            <select
              value={filters.method}
              onChange={(e) => onFilterChange({ ...filters, method: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">All Methods</option>
              {uniqueMethods.map((method) => (
                <option key={method} value={method}>{method}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Bank</label>
            <select
              value={filters.bank}
              onChange={(e) => onFilterChange({ ...filters, bank: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">All Banks</option>
              {uniqueBanks.map((bank) => (
                <option key={bank} value={bank}>{bank}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th scope="col" className="px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
              Site Fees
            </th>
            <th scope="col" className="px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
              Bank Fees
            </th>
            <th scope="col" className="px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
              Reseller Fees
            </th>
            <th scope="col" className="px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Timestamp
            </th>
            <th scope="col" className="px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Bank
            </th>
            <th scope="col" className="px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Method
            </th>
            <th scope="col" className="px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {transactions.map((transaction) => (
            <tr key={transaction.id}>
              <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-900">
                {formatAmount(transaction.amount)}
              </td>
              {/* Site Fees */}
              <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-900">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Flat:</span>
                    <span>{formatAmount(transaction.site_fee_flat || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rate:</span>
                    <span>{formatAmount(transaction.site_fee_rate || 0)}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span className="text-gray-500">Total:</span>
                    <span>{formatAmount(
                      (transaction.site_fee_flat || 0) + (transaction.site_fee_rate || 0)
                    )}</span>
                  </div>
                </div>
              </td>
              {/* Bank Fees */}
              <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-900">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Flat:</span>
                    <span>{formatAmount(transaction.bank_fee_flat || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rate:</span>
                    <span>{formatAmount(transaction.bank_fee_rate || 0)}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span className="text-gray-500">Total:</span>
                    <span>{formatAmount(
                      (transaction.bank_fee_flat || 0) + (transaction.bank_fee_rate || 0)
                    )}</span>
                  </div>
                </div>
              </td>
              {/* Reseller Fees */}
              <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-900">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Flat:</span>
                    <span>{formatAmount(transaction.reseller_fee_flat || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rate:</span>
                    <span>{formatAmount(transaction.reseller_fee_rate || 0)}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span className="text-gray-500">Total:</span>
                    <span>{formatAmount(
                      (transaction.reseller_fee_flat || 0) + (transaction.reseller_fee_rate || 0)
                    )}</span>
                  </div>
                </div>
              </td>
              <td className="px-8 py-6 whitespace-nowrap">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                  {transaction.status}
                </span>
              </td>
              <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-500">
                {formatDate(transaction.timestamp)}
              </td>
              <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-500">
                {transaction.bank}
              </td>
              <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-500">
                {transaction.method}
              </td>
              <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-500">
                {transaction.type}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing page <span className="font-medium">{currentPage}</span> of{' '}
              <span className="font-medium">{totalPages}</span>
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                title="Previous page"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Previous</span>
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              {getVisiblePages().map((page, index) => (
                typeof page === 'number' ? (
                  <button
                    key={index}
                    onClick={() => onPageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ) : (
                  <span
                    key={index}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                  >
                    {page}
                  </span>
                )
              ))}
              <button
                title="Next page"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Next</span>
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}